'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { useRole } from '@/lib/useRole';
import {
  LayoutDashboard,
  Building2,
  Home,
  Layers,
  Bird,
  Wheat,
  Stethoscope,
  Skull,
  Scale,
  Egg,
  Package,
  ShoppingCart,
  Truck,
  Receipt,
  Users,
  UserCheck,
  CheckSquare,
  Shield,
  Wrench,
  BarChart3,
  TrendingUp,
  Bell,
  FileText,
  ClipboardList,
  Settings,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  UserCircle,
  UserPlus,
  ClipboardCheck,
  FileDiff,
  BookOpen,
  Activity,
  ArrowRightLeft,
  ScanLine,
  Scissors,
  GitCommit,
  PackagePlus,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  href: string;
  badge?: number;
  group: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'nav-dashboard', label: 'Operations Dashboard', icon: LayoutDashboard, href: '/', group: 'main' },
  { id: 'nav-farms', label: 'Farms & Branches', icon: Building2, href: '/farms-branches', group: 'main' },
  { id: 'nav-houses', label: 'Poultry Houses', icon: Home, href: '/houses', group: 'main' },
  { id: 'nav-flocks', label: 'Flock Management', icon: Layers, href: '/flock-management', group: 'main' },
  { id: 'nav-birds', label: 'Bird Records', icon: Bird, href: '/bird-records', group: 'main' },
  { id: 'nav-birds-movements', label: 'Bird Movements', icon: Activity, href: '/bird-records/movements', group: 'main' },
  { id: 'nav-birds-placements', label: 'Bird Placements', icon: PackagePlus, href: '/bird-records/placements', group: 'main' },
  { id: 'nav-birds-daily', label: 'Daily Bird Records', icon: BookOpen, href: '/bird-records/daily', group: 'main' },
  { id: 'nav-birds-transfers', label: 'Bird Transfers', icon: ArrowRightLeft, href: '/bird-records/transfers', group: 'main' },
  { id: 'nav-birds-reconciliation', label: 'Reconciliation', icon: ScanLine, href: '/bird-records/reconciliation', group: 'main' },
  { id: 'nav-birds-adjustments', label: 'Pop. Adjustments', icon: GitCommit, href: '/bird-records/adjustments', group: 'main' },
  { id: 'nav-birds-culling', label: 'Bird Culling', icon: Scissors, href: '/bird-records/culling', group: 'main' },
  { id: 'nav-birds-sales', label: 'Bird Sales', icon: ShoppingCart, href: '/bird-records/sales', group: 'main' },
  { id: 'nav-birds-settings', label: 'Bird Records Settings', icon: Settings, href: '/bird-records/settings', group: 'main' },
  { id: 'nav-daily-records', label: 'Daily Records', icon: ClipboardList, href: '/daily-records', group: 'main' },
  { id: 'nav-feed', label: 'Feed Management', icon: Wheat, href: '/feed', group: 'operations' },
  { id: 'nav-health', label: 'Health & Vaccination', icon: Stethoscope, href: '/health', badge: 3, group: 'operations' },
  { id: 'nav-mortality', label: 'Mortality & Culling', icon: Skull, href: '/mortality-culling', group: 'operations' },
  { id: 'nav-growth', label: 'Growth & Weight', icon: Scale, href: '/growth', group: 'operations' },
  { id: 'nav-eggs', label: 'Egg Production', icon: Egg, href: '/egg-production', group: 'operations' },
  { id: 'nav-inventory', label: 'Inventory & Stores', icon: Package, href: '/inventory-stores', group: 'supply' },
  { id: 'nav-procurement', label: 'Procurement', icon: ShoppingCart, href: '/procurement', badge: 2, group: 'supply' },
  { id: 'nav-invoice-matching', label: 'Invoice Matching', icon: FileDiff, href: '/procurement/invoices', group: 'supply' },
  { id: 'nav-suppliers', label: 'Suppliers', icon: Truck, href: '/suppliers', group: 'supply' },
  { id: 'nav-supplier-onboarding', label: 'Supplier Onboarding', icon: ClipboardCheck, href: '/suppliers/onboarding', group: 'supply' },
  { id: 'nav-supplier-approvals', label: 'Supplier Approvals', icon: UserCheck, href: '/suppliers/approvals', group: 'supply' },
  { id: 'nav-sales', label: 'Sales Management', icon: Receipt, href: '/sales', group: 'commercial' },
  { id: 'nav-customers', label: 'Customers', icon: Users, href: '/customers', group: 'commercial' },
  { id: 'nav-expenses', label: 'Expenses', icon: DollarSign, href: '/expenses', group: 'commercial' },
  { id: 'nav-finance', label: 'Finance', icon: TrendingUp, href: '/finance', group: 'commercial' },
  { id: 'nav-employees', label: 'Employees', icon: UserCheck, href: '/employees', group: 'people' },
  { id: 'nav-payroll', label: 'Payroll', icon: DollarSign, href: '/payroll', group: 'people' },
  { id: 'nav-staff-invitations', label: 'Staff Invitations', icon: UserPlus, href: '/staff-invitations', group: 'people' },
  { id: 'nav-tasks', label: 'Tasks & Activities', icon: CheckSquare, href: '/tasks', badge: 5, group: 'people' },
  { id: 'nav-biosecurity', label: 'Biosecurity', icon: Shield, href: '/biosecurity', group: 'people' },
  { id: 'nav-assets', label: 'Assets & Equipment', icon: Wrench, href: '/assets', group: 'people' },
  { id: 'nav-reports', label: 'Reports', icon: BarChart3, href: '/reports', group: 'insights' },
  { id: 'nav-analytics', label: 'Analytics & Insights', icon: TrendingUp, href: '/analytics', group: 'insights' },
  { id: 'nav-notifications', label: 'Notifications', icon: Bell, href: '/notifications', badge: 7, group: 'insights' },
  { id: 'nav-documents', label: 'Documents', icon: FileText, href: '/documents', group: 'insights' },
  { id: 'nav-audit', label: 'Audit Logs', icon: ClipboardList, href: '/audit', group: 'insights' },
  { id: 'nav-settings', label: 'Settings', icon: Settings, href: '/settings', group: 'system' },
  { id: 'nav-profile', label: 'My Profile', icon: UserCircle, href: '/profile', group: 'system' },
];

const GROUP_LABELS: Record<string, string> = {
  main: 'Overview',
  operations: 'Farm Operations',
  supply: 'Supply Chain',
  commercial: 'Commercial',
  people: 'People & Safety',
  insights: 'Reports & Insights',
  system: 'System',
};

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();
  const { canNav } = useRole();

  const groups = Array.from(new Set(NAV_ITEMS.map((i) => i.group)));

  return (
    <aside
      className="flex flex-col h-full transition-all duration-300 ease-in-out"
      style={{ backgroundColor: 'var(--sidebar-bg)', width: collapsed ? '64px' : '240px', minWidth: collapsed ? '64px' : '240px' }}
    >
      {/* Logo */}
      <div
        className="flex items-center px-3 py-4 border-b"
        style={{ borderColor: 'var(--sidebar-border)', minHeight: '64px' }}
      >
        <div className="flex items-center gap-2 overflow-hidden">
          {collapsed ? (
            <img
              src="/assets/images/favicon-1788574387643.png"
              alt="Y77 Poultry"
              width={32}
              height={32}
              className="flex-shrink-0 object-contain"
            />
          ) : (
            <img
              src="/assets/images/favicon-1788574387643.png"
              alt="Y77 Poultry"
              height={36}
              className="flex-shrink-0 object-contain"
              style={{ maxHeight: '36px', width: 'auto' }}
            />
          )}
          {/* Brand name is included in the logo image */}
        </div>
        {!collapsed && (
          <button
            onClick={onToggle}
            className="ml-auto flex-shrink-0 p-1 rounded transition-colors"
            style={{ color: 'var(--sidebar-text-muted)' }}
            aria-label="Collapse sidebar"
          >
            <ChevronLeft size={16} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-2 scrollbar-thin" style={{ overflowX: 'hidden' }}>
        {groups.map((group) => {
          const items = NAV_ITEMS.filter((i) => i.group === group && canNav(i.id));
          if (items.length === 0) return null;
          return (
            <div key={`group-${group}`}>
              {!collapsed && (
                <div className="sidebar-section-label">{GROUP_LABELS[group]}</div>
              )}
              {collapsed && <div className="my-1 mx-2 border-t" style={{ borderColor: 'var(--sidebar-border)' }} />}
              {items.map((item) => {
                const Icon = item.icon;
                const isActive = pathname === item.href || (item.href !== '/' && pathname.startsWith(item.href));
                return (
                  <div key={item.id} className="relative group px-2">
                    <Link
                      href={item.href}
                      className={`sidebar-item ${isActive ? 'active' : ''}`}
                      style={{ justifyContent: collapsed ? 'center' : 'flex-start' }}
                    >
                      <Icon size={16} className="flex-shrink-0" />
                      {!collapsed && (
                        <>
                          <span className="truncate flex-1">{item.label}</span>
                          {item.badge && (
                            <span
                              className="flex-shrink-0 text-xs font-bold px-1.5 py-0.5 rounded-full"
                              style={{ backgroundColor: 'var(--danger)', color: '#fff', fontSize: '0.6rem' }}
                            >
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                    </Link>
                    {collapsed && (
                      <div
                        className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 rounded text-xs font-medium text-white pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150 whitespace-nowrap z-50"
                        style={{ backgroundColor: '#0f172a' }}
                      >
                        {item.label}
                        {item.badge && (
                          <span className="ml-1.5 px-1 py-0.5 rounded-full text-white" style={{ backgroundColor: 'var(--danger)', fontSize: '0.6rem' }}>
                            {item.badge}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Collapse toggle at bottom */}
      {collapsed && (
        <div className="px-2 py-3 border-t" style={{ borderColor: 'var(--sidebar-border)' }}>
          <button
            onClick={onToggle}
            className="w-full flex items-center justify-center p-2 rounded transition-colors"
            style={{ color: 'var(--sidebar-text-muted)' }}
            aria-label="Expand sidebar"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </aside>
  );
}